import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import {
  colorTextError,
  colorTextMuted,
  colorTextPrimary,
} from "src/design/tokens";
import { useGetSubscriptionTiers } from "src/data/landing/queries/subscriptionTiers";
import { SubscriptionTierCard } from "src/components/LandingPage/components/SubscriptionTierCard";

interface SubscriptionTiersModalProps {
  open: boolean;
  selectedTier: string | null;
  onClose: () => void;
  onSelect: (tierName: string) => void;
}

export const SubscriptionTiersModal = ({
  open,
  selectedTier,
  onClose,
  onSelect,
}: SubscriptionTiersModalProps) => {
  const { t } = useTranslation();
  const { data: tiers, isPending, isError } = useGetSubscriptionTiers();

  const handleSelect = (tierName: string) => {
    onSelect(tierName);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Typography
          component="span"
          sx={{
            display: "block",
            fontFamily: "Bogart-bold",
            fontWeight: 700,
            fontSize: 22,
            color: colorTextPrimary,
          }}
        >
          {t("landingPage.subscriptionTiers.modalTitle")}
        </Typography>
        <Typography
          component="span"
          sx={{ display: "block", fontSize: 14, color: colorTextMuted, mt: 0.5 }}
        >
          {t("landingPage.subscriptionTiers.modalSubtitle")}
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", top: 12, right: 12, color: colorTextMuted }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {isPending ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : isError || !tiers ? (
          <Typography
            sx={{ color: colorTextError, textAlign: "center", py: 6 }}
          >
            {t("landingPage.subscriptionTiers.loadError")}
          </Typography>
        ) : (
          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            {tiers.map((tier) => (
              <Grid item xs={12} sm={6} key={tier.name}>
                <SubscriptionTierCard
                  tier={tier}
                  selected={tier.name === selectedTier}
                  onSelect={() => handleSelect(tier.name)}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>
    </Dialog>
  );
};
